import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import { useApolloClient } from "@apollo/client/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Loader2, 
  AlertCircle, 
  CheckCircle2, 
  Building2, 
  User, 
  Shield,
  Eye,
  EyeOff,
  Check,
  X
} from "lucide-react";
import { VALIDATE_INVITATION } from "@/graphql/queries";
import { ACCEPT_INVITATION_WITH_PASSWORD } from "@/graphql/mutations";
import { setAuthCookie } from "@/lib/cookie-utils";
import { setScopedStorageItem } from "@/lib/session-utils";
import { saveTokens } from "@/lib/token-utils";

type PageState = "loading" | "invalid" | "form" | "success" | "submitting";

interface ValidateInvitationResponse {
  validateInvitation: {
    valid: boolean;
    email?: string;
    organizationName?: string;
    inviterName?: string;
    role?: string;
    expiresAt?: string;
    error?: string;
  };
}

interface AcceptInvitationWithPasswordResponse {
  acceptInvitationWithPassword: {
    success: boolean;
    user?: {
      id: string;
      email: string;
      name: string;
      onboardingCompleted: boolean;
    };
    organizations?: Array<{
      id: string;
      name: string;
      role: string;
      isPersonal: boolean;
    }>;
    tokens?: {
      accessToken: string;
      refreshToken: string;
      expiresIn: number;
    };
    requiresOnboarding: boolean;
    error?: string;
  };
}

// Password validation rules
const passwordRules = [
  { id: 'length', label: 'At least 8 characters', test: (p: string) => p.length >= 8 },
  { id: 'uppercase', label: 'One uppercase letter', test: (p: string) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter', test: (p: string) => /[a-z]/.test(p) },
  { id: 'digit', label: 'One digit', test: (p: string) => /\d/.test(p) },
];

const InviteSetup = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const apolloClient = useApolloClient();
  
  const [pageState, setPageState] = useState<PageState>("loading");
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  
  // Form state
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validate invitation
  const { data: inviteData, loading: inviteLoading, error: inviteError } = useQuery<ValidateInvitationResponse>(VALIDATE_INVITATION, {
    variables: { token },
    skip: !token,
    errorPolicy: 'all',
  });

  // Accept invitation with password mutation
  const [acceptInvitationWithPassword, { loading: submitting }] = useMutation<AcceptInvitationWithPasswordResponse>(ACCEPT_INVITATION_WITH_PASSWORD, {
    onCompleted: async (data) => {
      if (data.acceptInvitationWithPassword.success) {
        const { tokens } = data.acceptInvitationWithPassword;
        if (tokens) {
          saveTokens(tokens.accessToken, tokens.refreshToken);
          setAuthCookie(tokens.accessToken);
        }
        setScopedStorageItem('showOrgSwitcherTooltip', 'true');
        await apolloClient.resetStore();
        setPageState("success");
      } else {
        setError(data.acceptInvitationWithPassword.error || "Failed to create account");
        setPageState("form");
      }
    },
    onError: (err) => {
      setError(err.message || "Failed to create account");
      setPageState("form");
    },
  });

  // Determine page state based on query results
  useEffect(() => {
    if (inviteLoading) {
      setPageState("loading");
      return;
    }

    if (inviteError || !inviteData?.validateInvitation?.valid) {
      setError(inviteData?.validateInvitation?.error || "This invitation is invalid or has expired");
      setPageState("invalid");
      return;
    }

    setPageState("form");
  }, [inviteLoading, inviteData, inviteError]);

  // Success countdown
  useEffect(() => {
    if (pageState === "success" && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (pageState === "success" && countdown === 0) {
      navigate("/");
    }
  }, [pageState, countdown, navigate]);

  // Check password validity
  const passwordValidity = passwordRules.map(rule => ({
    ...rule,
    valid: rule.test(password)
  }));
  const isPasswordValid = passwordValidity.every(r => r.valid);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }
    
    if (!isPasswordValid) {
      setError("Please ensure your password meets all requirements");
      return;
    }
    
    if (!passwordsMatch) {
      setError("Passwords don't match");
      return;
    }

    setError(null);
    setPageState("submitting");
    
    await acceptInvitationWithPassword({
      variables: {
        input: {
          token,
          name: name.trim(),
          password,
        },
      },
    });
  };

  const invitation = inviteData?.validateInvitation;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="w-full max-w-md space-y-8 animate-fade-in">
        {/* Logo */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <img
              src="/Logo White Transparent.png"
              alt="FlowLabs Logo"
              className="h-16 w-auto"
            />
          </div>
        </div>

        {/* Loading State */}
        {pageState === "loading" && (
          <Card className="p-8 border-border shadow-xl">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Validating invitation...</p>
            </div>
          </Card>
        )}

        {/* Invalid/Expired State */}
        {pageState === "invalid" && (
          <Card className="p-8 border-border shadow-xl">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold">Invitation Invalid</h2>
                <p className="text-muted-foreground">
                  {error || "This invitation has expired or is no longer valid."}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                Contact the person who invited you to request a new invitation.
              </p>
              <Button asChild className="w-full">
                <Link to="/login">Go to Login</Link>
              </Button>
            </div>
          </Card>
        )}

        {/* Setup Form State */}
        {(pageState === "form" || pageState === "submitting") && invitation && (
          <Card className="p-8 border-border shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold">Set Up Your Account</h2>
                <p className="text-muted-foreground">
                  Create your FlowLabs account to join{" "}
                  <span className="font-semibold text-foreground">{invitation.organizationName}</span>
                </p>
              </div>

              <div className="space-y-3 py-4 border-y border-border">
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Organization:</span>
                  <span className="font-medium">{invitation.organizationName}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Role:</span>
                  <span className="font-medium capitalize">{invitation.role?.toLowerCase()}</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium">{invitation.email}</span>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                {/* Name Field */}
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Enter your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    disabled={pageState === "submitting"}
                  />
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={pageState === "submitting"}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {/* Password Requirements */}
                  {password.length > 0 && (
                    <div className="grid grid-cols-2 gap-1 mt-2">
                      {passwordValidity.map((rule) => (
                        <div
                          key={rule.id}
                          className={`flex items-center gap-1.5 text-xs ${
                            rule.valid ? 'text-green-500' : 'text-muted-foreground'
                          }`}
                        >
                          {rule.valid ? (
                            <Check className="w-3 h-3" />
                          ) : (
                            <X className="w-3 h-3" />
                          )}
                          {rule.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={pageState === "submitting"}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmPassword.length > 0 && (
                    <p className={`text-xs ${passwordsMatch ? 'text-green-500' : 'text-destructive'}`}>
                      {passwordsMatch ? (
                        <span className="flex items-center gap-1">
                          <Check className="w-3 h-3" />
                          Passwords match
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <X className="w-3 h-3" />
                          Passwords don't match
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12"
                disabled={pageState === "submitting" || !isPasswordValid || !passwordsMatch || !name.trim()}
              >
                {pageState === "submitting" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  "Create Account & Join"
                )}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                Already have an account?{" "}
                <Link to={`/invite/${token}`} className="text-primary hover:underline">
                  Sign in instead
                </Link>
              </p>
            </form>
          </Card>
        )}

        {/* Success State */}
        {pageState === "success" && invitation && (
          <Card className="p-8 border-border shadow-xl">
            <div className="flex flex-col items-center text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl font-bold">Welcome to {invitation.organizationName}!</h2>
                <p className="text-muted-foreground">
                  Your account has been created and you've joined as a {invitation.role?.toLowerCase()}.
                </p>
              </div>
              <div className="space-y-2">
                <Button className="w-full" onClick={() => navigate("/")}>
                  Go to Dashboard
                </Button>
                <p className="text-sm text-muted-foreground">
                  Redirecting in {countdown} seconds...
                </p>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default InviteSetup;
